import { HttpApi } from "./api";
import { HttpEndpoint } from "./endpoint";
import { WebSocket } from "./websocket";

export namespace Http {
  export type Client = {
    request(url: URL, options: {
      method?: string,
      headers?: Record<string, string>,
      body?: null | ReadableStream<Uint8Array<ArrayBuffer>>
    }): Promise<Response>
  };

  export type Handler = (request: Request) => Promise<Response>;

  export type Route = {
    path: string,
    // If a route has a null method, it will receive all requests
    // unless there is a more specific route
    method: null | string,

    handler: Handler,
  }

  export type Request = {
    query: Record<string, string>,
    path: string,
    method: string,

    headers: Record<string, void | string | string[]>,
    body: null | ReadableStream<Uint8Array>
  }

  export type Response = {
    status: number,

    headers: Record<string, void | string | string[]>,
    body: null | ReadableStream<Uint8Array>
  }
}

export const Http = {
  get endpoint() { return HttpEndpoint },
  get api() { return HttpApi },
  get ws() { return WebSocket },

  /**
   * Combine an array of routes into a single HTTP Handler, that calls
   * the relevant route handler based on their path/method.
   * 
   * @param routes A list of all routes
   * @param notFoundHandler A fallback handler that is called if there is no matched route
   * @returns Http.handler
   */
  createRoutesHandler(routes: Http.Route[], notFoundHandler: Http.Handler): Http.Handler {
    const routeByPath = new Map<string, Http.Route[]>();
    for (const route of routes) {
      const routesInPath = routeByPath.get(route.path) || [];

      routesInPath.push(route);

      routeByPath.set(route.path, routesInPath);
    }

    return async (request) => {
      const requestPath = request.path.toLowerCase().split('/').filter(Boolean).join('/');

      const routesInPath = routeByPath.get(requestPath) || [];
      
      // try to find via matching method
      const matchingMethodRoute = routesInPath
        .find(route => route.method && (route.method === request.method));

      if (matchingMethodRoute) {
        return await matchingMethodRoute.handler(request);
      }

      // fallback: null methods count as wildcards. let see if we can find any.
      const matchingAnyRoute = routesInPath.find(route => route.method === null)
      if (matchingAnyRoute) {
        return await matchingAnyRoute.handler(request);
      }

      // fallback: serve the not found handler
      return await notFoundHandler(request);
    }
  }
};
